import React, { useEffect, useState, useCallback } from 'react';
import { reviewsAPI } from '../../utils/api';
import RatingStars from './RatingStars';
import ReviewCard from './ReviewCard';
import ReviewForm from './ReviewForm';
import { Star, Camera, SlidersHorizontal } from 'lucide-react';

const ReviewSection = ({ productId }) => {
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [sortBy, setSortBy] = useState('newest');
  const [ratingFilter, setRatingFilter] = useState(null);
  const [hasMedia, setHasMedia] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await reviewsAPI.getSummary(productId);
      setSummary(data);
    } catch (_) {}
  }, [productId]);

  const loadReviews = useCallback(async (pg = 1) => {
    try {
      const params = { sort_by: sortBy, page: pg, limit: 5 };
      if (ratingFilter) params.rating_filter = ratingFilter;
      if (hasMedia) params.has_media = true;
      const { data } = await reviewsAPI.getForProduct(productId, params);
      if (pg === 1) {
        setReviews(data.reviews);
      } else {
        setReviews(prev => [...prev, ...data.reviews]);
      }
      setTotal(data.total);
      setPage(data.page);
      setPages(data.pages);
    } catch (_) {}
  }, [productId, sortBy, ratingFilter, hasMedia]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadReviews(1); }, [loadReviews]);

  const handleLoadMore = () => { if (page < pages) loadReviews(page + 1); };

  const handleReviewSubmitted = () => {
    loadSummary();
    loadReviews(1);
  };

  const handleRatingFilter = (r) => {
    setRatingFilter(ratingFilter === r ? null : r);
  };

  if (!summary) return null;

  const maxDistCount = Math.max(...Object.values(summary.distribution), 1);

  return (
    <section className="mt-16 pt-16 border-t border-[#EBE4D5]" id="reviews" data-testid="review-section">
      <h2 className="font-cormorant text-2xl sm:text-3xl lg:text-4xl mb-10">Ratings & Reviews</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
        {/* Rating Summary */}
        <div className="lg:col-span-1" data-testid="rating-summary">
          {summary.total_reviews > 0 ? (
            <div className="text-center lg:text-left">
              <div className="flex items-baseline gap-2 justify-center lg:justify-start mb-2">
                <span className="font-cormorant text-5xl text-[#1A1A18]">{summary.average_rating}</span>
                <span className="font-outfit text-sm text-[#8C8577]">out of 5</span>
              </div>
              <RatingStars rating={Math.round(summary.average_rating)} size="md" />
              <p className="font-outfit text-sm text-[#8C8577] mt-2">Based on {summary.total_reviews} review{summary.total_reviews !== 1 ? 's' : ''}</p>

              {/* Distribution Bars */}
              <div className="mt-6 space-y-2">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = summary.distribution[String(star)] || 0;
                  const pct = maxDistCount > 0 ? (count / maxDistCount) * 100 : 0;
                  const isActive = ratingFilter === star;
                  return (
                    <button
                      key={star}
                      onClick={() => handleRatingFilter(star)}
                      className={`flex items-center gap-2 w-full group ${isActive ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
                      data-testid={`rating-filter-${star}`}
                    >
                      <span className="font-outfit text-xs w-3">{star}</span>
                      <Star className="w-3 h-3 fill-[#C29B57] text-[#C29B57]" />
                      <div className="flex-1 h-2 bg-[#EBE4D5] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${isActive ? 'bg-[#1A1A18]' : 'bg-[#C29B57]'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="font-outfit text-xs text-[#8C8577] w-6 text-right">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center lg:text-left py-6" data-testid="no-reviews">
              <p className="font-cormorant text-2xl text-[#8C8577] mb-2">No Reviews Yet</p>
              <p className="font-outfit text-sm text-[#8C8577]">Be the first to review this product</p>
            </div>
          )}
        </div>

        {/* Write Review + Filters */}
        <div className="lg:col-span-2">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <ReviewForm productId={productId} onReviewSubmitted={handleReviewSubmitted} />
            <div className="flex items-center gap-3">
              <button
                onClick={() => setHasMedia(!hasMedia)}
                className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-md font-outfit text-xs transition-colors ${hasMedia ? 'border-[#C29B57] bg-[#C29B57]/10 text-[#C29B57]' : 'border-[#EBE4D5] text-[#8C8577]'}`}
                data-testid="filter-media"
              >
                <Camera className="w-3.5 h-3.5" /> With Photos
              </button>
              <div className="flex items-center gap-1.5">
                <SlidersHorizontal className="w-3.5 h-3.5 text-[#8C8577]" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-[#EBE4D5] rounded-md px-2 py-1.5 font-outfit text-xs outline-none focus:border-[#C29B57]"
                  data-testid="review-sort"
                >
                  <option value="newest">Most Recent</option>
                  <option value="highest">Highest Rated</option>
                  <option value="lowest">Lowest Rated</option>
                  <option value="helpful">Most Helpful</option>
                </select>
              </div>
            </div>
          </div>

          {/* Active Filters */}
          {(ratingFilter || hasMedia) && (
            <div className="flex gap-2 mb-4 flex-wrap">
              {ratingFilter && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#C29B57]/10 border border-[#C29B57]/30 rounded-full font-outfit text-xs text-[#C29B57]">
                  {ratingFilter} Star{ratingFilter > 1 ? 's' : ''}
                  <button onClick={() => setRatingFilter(null)} className="ml-1 hover:text-[#99733D]">×</button>
                </span>
              )}
              {hasMedia && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#C29B57]/10 border border-[#C29B57]/30 rounded-full font-outfit text-xs text-[#C29B57]">
                  With Photos
                  <button onClick={() => setHasMedia(false)} className="ml-1 hover:text-[#99733D]">×</button>
                </span>
              )}
            </div>
          )}

          {/* Review List */}
          {reviews.length > 0 ? (
            <div data-testid="reviews-list">
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}

              {page < pages && (
                <button
                  onClick={handleLoadMore}
                  className="w-full mt-4 py-3 border border-[#EBE4D5] hover:border-[#C29B57] rounded-md font-outfit text-sm text-[#8C8577] hover:text-[#C29B57] transition-colors"
                  data-testid="load-more-reviews"
                >
                  Load More Reviews ({total - reviews.length} remaining)
                </button>
              )}
            </div>
          ) : (
            <p className="font-outfit text-sm text-[#8C8577] py-8 text-center" data-testid="no-reviews-filtered">
              {ratingFilter || hasMedia ? 'No reviews match this filter' : 'No reviews yet. Be the first!'}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default ReviewSection;
